import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Calendar, Clock, DollarSign, User, ArrowLeft, CheckCircle, MapPin, Star, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingPage() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { user, token, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({ weekly_slots: [], booked_slots: [], buffer_minutes: 15 });
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchProviderData();
  }, [providerId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchProviderData = async () => {
    try {
      const [profileRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduler/profile/${providerId}`),
        axios.get(`${API_URL}/api/scheduler/services/public/${providerId}`)
      ]);
      setProfile(profileRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to fetch provider data:', error);
      toast.error('Provider not found or not accepting bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (date) => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduler/availability/public/${providerId}?date=${date}`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const getAvailableTimeSlots = () => {
    if (!selectedDate || !selectedService) return [];

    const date = new Date(selectedDate);
    const dayOfWeek = (date.getDay() + 6) % 7; // Convert to Mon=0 format
    
    const daySlots = availability.weekly_slots.filter(s => s.day_of_week === dayOfWeek && s.is_active);
    if (daySlots.length === 0) return [];

    const slots = [];
    const serviceDuration = selectedService.duration;
    const buffer = availability.buffer_minutes;

    daySlots.forEach(slot => {
      const [startH, startM] = slot.start_time.split(':').map(Number);
      const [endH, endM] = slot.end_time.split(':').map(Number);
      
      let currentMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;

      while (currentMinutes + serviceDuration <= endMinutes) {
        const timeStr = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
        const endTimeStr = `${Math.floor((currentMinutes + serviceDuration) / 60).toString().padStart(2, '0')}:${((currentMinutes + serviceDuration) % 60).toString().padStart(2, '0')}`;
        
        // Check if slot is already booked
        const isBooked = availability.booked_slots.some(([bookedStart, bookedEnd]) => {
          return timeStr < bookedEnd && endTimeStr > bookedStart;
        });

        if (!isBooked) {
          slots.push(timeStr);
        }

        currentMinutes += 30; // 30-minute increments
      }
    });

    return slots;
  };

  const handleBooking = async () => {
    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error('Please select a service, date, and time');
      return;
    }

    // Guest validation
    if (!user) {
      if (!guestName.trim() || !guestEmail.trim()) {
        toast.error('Please provide your name and email to book as a guest');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestEmail.trim())) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    setBooking(true);
    try {
      const payload = {
        provider_id: providerId,
        service_id: selectedService.id,
        booking_date: selectedDate,
        start_time: selectedTime,
        client_notes: clientNotes,
      };
      if (!user) {
        payload.guest_name = guestName.trim();
        payload.guest_email = guestEmail.trim();
      }
      const headers = user ? getAuthHeader() : {};
      await axios.post(`${API_URL}/api/scheduler/bookings`, payload, { headers });

      setBookingSuccess(true);
      toast.success('Booking request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);
    return maxDate.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center">
        <div className="text-center">
          <User className="mx-auto text-slate-300 mb-4" size={64} />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Provider Not Found</h2>
          <p className="text-slate-600 mb-4">This booking page is not available</p>
          <button onClick={() => navigate('/')} className="text-indigo-600 hover:text-indigo-700">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Booking Request Sent!</h2>
          <p className="text-slate-600 mb-6">
            Your appointment request has been sent to {profile.display_name}. You'll receive a confirmation once they approve it.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-slate-600"><strong>Service:</strong> {selectedService?.name}</p>
            <p className="text-sm text-slate-600"><strong>Date:</strong> {selectedDate}</p>
            <p className="text-sm text-slate-600"><strong>Time:</strong> {selectedTime}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/scheduler')}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              View My Bookings
            </button>
            <button
              onClick={() => {
                setBookingSuccess(false);
                setSelectedService(null);
                setSelectedDate('');
                setSelectedTime('');
                setClientNotes('');
              }}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Book Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Provider Info */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
              {/* Profile Photo */}
              <div className="flex flex-col items-center text-center mb-6">
                {profile.profile_photo_url ? (
                  <img
                    src={profile.profile_photo_url.startsWith('http') ? profile.profile_photo_url : `${API_URL}${profile.profile_photo_url}`}
                    alt={profile.display_name}
                    className="w-24 h-24 rounded-full object-cover mb-4 border-4 border-indigo-100"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <User className="text-indigo-600" size={40} />
                  </div>
                )}
                <h2 className="text-xl font-semibold text-slate-900">{profile.display_name}</h2>
                {profile.user_name && profile.user_name !== profile.display_name && (
                  <p className="text-sm text-slate-500">{profile.user_name}</p>
                )}
              </div>

              {profile.bio && (
                <p className="text-slate-600 text-sm mb-6">{profile.bio}</p>
              )}

              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-slate-400" />
                  {profile.timezone}
                </div>
              </div>

              {/* Calendly Embed Option */}
              {profile.calendly_url && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-3">Or book via Calendly:</p>
                  <a
                    href={profile.calendly_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full"
                  >
                    <Calendar size={18} />
                    Open Calendly
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Booking Form */}
          <div className="md:col-span-2 space-y-6">
            {/* Services */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Select a Service</h3>
              
              {services.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No services available at this time</p>
              ) : (
                <div className="space-y-3">
                  {services.map(service => (
                    <button
                      key={service.id}
                      onClick={() => setSelectedService(service)}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        selectedService?.id === service.id
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid="service-option"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">{service.name}</h4>
                          <p className="text-sm text-indigo-600 mb-1">{service.category}</p>
                          <p className="text-sm text-slate-600">{service.description}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="font-semibold text-slate-900">
                            {service.price > 0 ? `$${service.price}` : 'Free'}
                          </p>
                          <p className="text-sm text-slate-500">{service.duration} min</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date & Time Selection */}
            {selectedService && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Select Date & Time</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Date Picker */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedTime('');
                      }}
                      min={getMinDate()}
                      max={getMaxDate()}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      data-testid="date-picker"
                    />
                  </div>

                  {/* Time Slots */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Time</label>
                    {selectedDate ? (
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                        {getAvailableTimeSlots().length > 0 ? (
                          getAvailableTimeSlots().map(time => (
                            <button
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                                selectedTime === time
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              data-testid="time-slot"
                            >
                              {time}
                            </button>
                          ))
                        ) : (
                          <p className="col-span-3 text-slate-500 text-sm py-4 text-center">
                            No available slots for this date
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-4 text-center">Select a date first</p>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                  <textarea
                    value={clientNotes}
                    onChange={(e) => setClientNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    placeholder="Any special requests or information for the provider..."
                    data-testid="booking-notes"
                  />
                </div>

                {/* Guest info (only if not logged in) */}
                {!user && (
                  <div className="mt-6 p-4 bg-indigo-50/60 border border-indigo-200 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-900">Booking as a guest</h4>
                      <button
                        type="button"
                        onClick={() => navigate('/login', { state: { from: `/book/${providerId}` } })}
                        className="text-xs text-indigo-600 hover:text-indigo-700 underline"
                        data-testid="login-instead-link"
                      >
                        Have an account? Log in
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Your Name *</label>
                        <input
                          type="text"
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Jane Doe"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          data-testid="guest-name-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Your Email *</label>
                        <input
                          type="email"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder="jane@example.com"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          data-testid="guest-email-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Book Button */}
                <div className="mt-6">
                  <button
                    onClick={handleBooking}
                    disabled={!selectedTime || booking}
                    className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="book-now-btn"
                  >
                    {booking ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} />
                        Request Booking
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
